// === ValidationDrawer ===
// Slide-in drawer scoped to the CanvasWorkflowPanel that surfaces the
// deterministic Workflow Validator's findings. Coexists with the
// AI-side Pedagogy Reviewer (commit 50be5164) — same drawer pattern,
// different source of truth (LLM vs deterministic graph traversal).
//
// Each issue row carries:
//   - severity (warn | fix) → border accent + icon color
//   - code (UNREACHABLE_ITEM | MISSING_END | …) → category badge
//   - message → human-readable summary
//   - targetItemId → optional click-to-focus on the matching node
//   - suggestedFix → optional { label, actionCreator }; renders a Fix button
//     that dispatches actionCreator(ctx) where ctx is the bag passed in via
//     the `fixContext` prop. Keeping the context external means the issue
//     payload stays pure (no store closures captured at validate time).
import React from "react";
import "./ValidationDrawer.scss";

// Icon + className per severity. Reuses material-icons so we don't depend
// on a new icon font.
const SEVERITY_META = {
  fix: { icon: "error_outline", className: "validation-drawer__row--fix" },
  warn: { icon: "warning_amber", className: "validation-drawer__row--warn" },
  info: { icon: "info_outline", className: "validation-drawer__row--info" },
};

function severityMeta(sev) {
  return SEVERITY_META[sev] || SEVERITY_META.info;
}

export default function ValidationDrawer({
  show,
  issues,
  onClose,
  onFocusItem,
  onApplyFix,
  fixContext,
}) {
  if (!show) return null;
  const list = Array.isArray(issues) ? issues : [];

  const focus = (targetItemId) => {
    if (!targetItemId || typeof onFocusItem !== "function") return;
    onFocusItem(targetItemId);
  };

  // Apply a suggestedFix: invoke its actionCreator with the per-issue context
  // bag (merges drawer-level fixContext + issue-level meta), then hand the
  // resulting thunk to the parent via onApplyFix so it can be dispatched
  // through redux-thunk. Keeping dispatch in the parent keeps this component
  // store-agnostic (testable without a Provider).
  const applyFix = (iss) => {
    if (!iss || !iss.suggestedFix || typeof iss.suggestedFix.actionCreator !== "function") return;
    if (typeof onApplyFix !== "function") return;
    const ctx = {
      ...(fixContext || {}),
      itemId: iss.targetItemId || null,
      triggerId: (iss.meta && iss.meta.triggerId) || null,
    };
    const thunk = iss.suggestedFix.actionCreator(ctx);
    onApplyFix(thunk);
  };

  return (
    <aside
      className="validation-drawer"
      role="complementary"
      aria-label="Workflow validation"
      data-testid="validation-drawer"
    >
      <header className="validation-drawer__head">
        <div className="validation-drawer__title">
          <i className="material-icons">rule</i>
          Workflow validator
          <span className="validation-drawer__sub">
            {list.length === 0
              ? "no issues"
              : `${list.length} issue${list.length === 1 ? "" : "s"}`}
          </span>
        </div>
        <button
          type="button"
          className="validation-drawer__close"
          onClick={onClose}
          aria-label="Close"
        >
          <i className="material-icons">close</i>
        </button>
      </header>
      <div className="validation-drawer__body">
        {list.length === 0 && (
          <div className="validation-drawer__state validation-drawer__state--empty">
            <i className="material-icons">check_circle_outline</i>
            No structural issues — workflow looks well-formed.
          </div>
        )}
        {list.length > 0 && (
          <ul className="validation-drawer__list" data-testid="validation-drawer__list">
            {list.map((iss, idx) => {
              const meta = severityMeta(iss.severity);
              const clickable = Boolean(iss.targetItemId);
              return (
                <li
                  key={`${iss.code}-${iss.targetItemId || "_"}-${idx}`}
                  className={`validation-drawer__row ${meta.className}${
                    clickable ? " validation-drawer__row--clickable" : ""
                  }`}
                  data-testid="validation-drawer__row"
                  onClick={clickable ? () => focus(iss.targetItemId) : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            focus(iss.targetItemId);
                          }
                        }
                      : undefined
                  }
                  tabIndex={clickable ? 0 : -1}
                  role={clickable ? "button" : "listitem"}
                >
                  <i className="material-icons validation-drawer__row-icon">{meta.icon}</i>
                  <div className="validation-drawer__row-body">
                    <div className="validation-drawer__row-head">
                      <span className="validation-drawer__row-category">{iss.code}</span>
                      <span className="validation-drawer__row-severity">{iss.severity}</span>
                    </div>
                    <div className="validation-drawer__row-message">{iss.message}</div>
                    {iss.targetItemId && (
                      <div className="validation-drawer__row-target">→ {iss.targetItemId}</div>
                    )}
                    {iss.suggestedFix && iss.suggestedFix.label && (
                      <div className="validation-drawer__row-actions">
                        <button
                          type="button"
                          className="validation-drawer__fix-btn"
                          data-testid="validation-drawer__fix-btn"
                          onClick={(e) => {
                            // Stop the row's onClick (focus-item) from firing
                            // when the user clicks the fix button.
                            e.stopPropagation();
                            applyFix(iss);
                          }}
                          aria-label={iss.suggestedFix.label}
                          title={iss.suggestedFix.label}
                        >
                          <i className="material-icons">auto_fix_high</i>
                          <span>{iss.suggestedFix.label}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}
